// ── Elegir a qué hora sale el mensaje ────────────────────────────────────────
// 2026-09-21, pedido del cliente.
//
// Quien atiende prepara de noche lo que tiene que salir a las nueve, y deja de
// depender de estar frente a la pantalla a esa hora.
//
// LAS DOS DECISIONES DE ESTA PANTALLA
// ───────────────────────────────────
//  1. Atajos primero, calendario después. Casi todo lo que se programa es
//     "en un rato" o "mañana a primera hora"; obligar a elegir día y hora en
//     un calendario para eso convierte dos clics en seis.
//  2. El aviso de la ventana se ve ANTES de programar, no al fallar. Si la
//     hora elegida cae después de que cierren las 24 horas, el mensaje no va
//     a poder salir — y enterarse a las tres de la mañana, cuando el envío
//     falla, no le sirve a nadie. El servidor lo valida igual; esto es para
//     que no haga falta chocarse.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { Select } from '../ui/Select.js';
import { DatePicker } from '../ui/DatePicker.js';
import { Chip } from '../ui/Chip.js';
import { useToast } from '../ui/Toast.js';
import { ApiError } from '../../lib/api.js';
import { useProgramarMensaje } from '../../hooks/useDmInbox.js';

export interface ProgramarEnvioProps {
  open: boolean;
  onClose: () => void;
  threadId: number;
  /** Lo que ya escribió. Sale tal cual a la hora elegida. */
  body: string;
  attachmentIds: number[];
  replyToPlatformId?: string | null;
  /** Cuántas horas quedan de ventana. null = no se sabe o no aplica. */
  hoursLeft?: number | null;
  /** Se llama cuando quedó programado, para limpiar el compositor. */
  onProgramado: () => void;
}

/** Los cuartos de hora del día. Programar "9:07" no es algo que nadie quiera,
 *  y una lista de 96 opciones se recorre más rápido que una de 1.440. */
function horasDelDia(): Array<{ value: string; label: string }> {
  const opciones: Array<{ value: string; label: string }> = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      opciones.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
    }
  }
  return opciones;
}

/** Une el día elegido con la hora elegida, en la zona horaria de quien mira.
 *  Se manda en ISO: el servidor no tiene por qué saber dónde está nadie. */
function juntar(dia: Date, hora: string): Date {
  const [h, m] = hora.split(':').map(Number);
  const d = new Date(dia);
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d;
}

function aHora(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(Math.floor(d.getMinutes() / 15) * 15).padStart(2, '0')}`;
}

export function ProgramarEnvio({
  open, onClose, threadId, body, attachmentIds, replyToPlatformId, hoursLeft, onProgramado,
}: ProgramarEnvioProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const programar = useProgramarMensaje();

  const [dia, setDia] = useState<Date | null>(new Date());
  const [hora, setHora] = useState<string | null>(aHora(new Date(Date.now() + 3_600_000)));

  const cuando = useMemo(() => (dia && hora ? juntar(dia, hora) : null), [dia, hora]);

  /** Cuándo cierra la ventana, según las horas que quedan. */
  const cierra = useMemo(
    () => (hoursLeft != null && hoursLeft > 0 ? new Date(Date.now() + hoursLeft * 3_600_000) : null),
    [hoursLeft],
  );

  const fueraDeVentana = cuando != null && cierra != null && cuando.getTime() >= cierra.getTime();
  const enElPasado = cuando != null && cuando.getTime() <= Date.now() + 60_000;

  /** Los atajos: lo que de verdad se elige casi siempre. */
  const atajos = useMemo(() => {
    const ahora = new Date();
    const enUnaHora = new Date(ahora.getTime() + 3_600_000);

    const estaTarde = new Date(ahora);
    estaTarde.setHours(15, 0, 0, 0);

    const mañana = new Date(ahora);
    mañana.setDate(mañana.getDate() + 1);
    mañana.setHours(9, 0, 0, 0);

    return [
      { clave: 'inOneHour', etiqueta: t('messages.schedule.inOneHour', 'En 1 hora'), fecha: enUnaHora },
      { clave: 'thisAfternoon', etiqueta: t('messages.schedule.thisAfternoon', 'Esta tarde, 3:00 p. m.'), fecha: estaTarde },
      { clave: 'tomorrowMorning', etiqueta: t('messages.schedule.tomorrowMorning', 'Mañana, 9:00 a. m.'), fecha: mañana },
    ].filter(a => a.fecha.getTime() > ahora.getTime() + 60_000);
  }, [t]);

  function elegirAtajo(fecha: Date) {
    setDia(fecha);
    setHora(aHora(fecha));
  }

  async function confirmar() {
    if (!cuando) return;
    try {
      await programar.mutateAsync({
        threadId,
        ...(body.trim() ? { body: body.trim() } : {}),
        attachmentIds,
        replyToPlatformId: replyToPlatformId ?? null,
        scheduledFor: cuando.toISOString(),
      });
      toast.success(t('messages.schedule.done', 'Programado para {{cuando}}', {
        cuando: cuando.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
      }));
      onProgramado();
      onClose();
    } catch (err) {
      // El servidor explica qué hacer ("elige una hora anterior, o una
      // plantilla"). Ese texto es el que sirve, no uno nuestro.
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('messages.schedule.title', 'Programar el envío')}
      size="sm"
      footer={(
        <div style={{ display: 'flex', gap: 'var(--evo-space-2)', justifyContent: 'flex-end' }}>
          <Button variant="ghost" tone="muted" size="sm" onClick={onClose}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="solid" tone="cyan" size="sm"
            loading={programar.isPending}
            disabled={!cuando || fueraDeVentana || enElPasado}
            onClick={() => { void confirmar(); }}
          >
            {t('messages.schedule.confirm', 'Programar')}
          </Button>
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
          {atajos.map(a => (
            <Chip
              key={a.clave}
              label={a.etiqueta}
              size="sm"
              selected={cuando != null && Math.abs(cuando.getTime() - a.fecha.getTime()) < 60_000}
              onClick={() => elegirAtajo(a.fecha)}
            />
          ))}
        </div>

        <div style={{ display: 'flex', gap: 'var(--evo-space-3)', alignItems: 'flex-end' }}>
          <DatePicker
            mode="single"
            value={dia}
            onChange={setDia}
            label={t('messages.schedule.day', 'Día')}
            minDate={new Date()}
            size="sm"
            fullWidth
          />
          <Select
            value={hora}
            onChange={setHora}
            options={horasDelDia()}
            label={t('messages.schedule.time', 'Hora')}
            size="sm"
            searchable
          />
        </div>

        {/* Lo que Meta permite a esa hora. Se dice acá, que es cuando todavía
            se puede elegir otra. */}
        {fueraDeVentana && (
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
            {t('messages.schedule.afterWindow',
              'A esa hora la ventana de 24 horas ya estará cerrada y el mensaje no podrá salir. Elige una hora anterior, o mándalo con una plantilla aprobada.')}
          </p>
        )}
        {enElPasado && !fueraDeVentana && (
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
            {t('messages.schedule.inThePast', 'Esa hora ya pasó. Elige uno o dos minutos más adelante.')}
          </p>
        )}
        {!fueraDeVentana && !enElPasado && cierra && (
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
            {t('messages.schedule.windowCloses', 'La ventana de 24 horas cierra {{cuando}}.', {
              cuando: cierra.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }),
            })}
          </p>
        )}
      </div>
    </Modal>
  );
}
